import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Settings as SettingsIcon } from "lucide-react";
import { cn } from "@/cn";
import { useAuth } from "@/hooks/useAuth";

function initialsOf(name: string | undefined, email: string | undefined): string {
  const source = (name ?? email ?? "").trim();
  if (!source) return "?";
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

/**
 * Account menu — the way into Settings now that it is no longer a nav item.
 * Rendered in the sidebar on desktop and the bottom bar on small screens, so
 * Settings stays reachable at every width.
 */
export function ProfileMenu({
  collapsed,
  variant = "sidebar",
  onNavigate,
}: {
  collapsed?: boolean;
  variant?: "sidebar" | "compact";
  onNavigate?: () => void;
}) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const name = user?.name ?? user?.email;
  const initials = initialsOf(user?.name, user?.email).toUpperCase();
  const inSettings = pathname.startsWith("/settings");

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const avatar = (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand",
        variant === "compact" ? "size-6" : "size-7",
      )}
      aria-hidden
    >
      {initials}
    </span>
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
        title={name ?? "Account"}
        className={cn(
          "flex items-center rounded-md transition-colors",
          variant === "compact"
            ? "h-full w-full flex-col justify-center gap-0.5 text-[10px] font-medium"
            : "w-full gap-2 px-2 py-2 text-sm",
          variant === "compact" && (open || inSettings)
            ? "text-brand"
            : "text-muted-foreground hover:text-foreground",
          variant === "sidebar" && "hover:bg-surface-hover",
          variant === "sidebar" && collapsed && "justify-center px-0",
        )}
      >
        {avatar}
        {variant === "compact" && <span>Account</span>}
        {variant === "sidebar" && !collapsed && (
          <span className="truncate text-left text-foreground">{name}</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className={cn(
            "absolute z-[120] min-w-[220px] rounded-md border border-border bg-popover py-1.5 shadow-xl ring-1 ring-black/5 dark:ring-white/10",
            variant === "compact"
              ? "bottom-full left-1/2 mb-2 -translate-x-1/2"
              : "bottom-full left-0 mb-2",
          )}
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium text-foreground">
              {user?.name ?? "Signed in"}
            </p>
            {user?.email && (
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
          <div className="flex flex-col p-1">
            <Link
              to="/settings/account"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted hover:bg-surface-hover hover:text-foreground"
            >
              <SettingsIcon className="size-4" />
              <span>Settings</span>
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                signOut();
              }}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-muted hover:bg-surface-hover hover:text-foreground"
            >
              <LogOut className="size-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
